import { Grid, Paper, alpha, Box, Skeleton } from "@mui/material";
import React, { Fragment, useEffect } from "react";
import { theme } from "../../../../theme";
import { POSDashboardCard } from "../../components/dashboard.card";
import { posService } from "../../../../services/pos.service";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const formatNumber = (value: number): string => {
  return Number(value.toFixed(3)).toString();
};

export const POSDashboardCards: React.FC<{
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}> = ({ dateRange }) => {
  const [cardsLoading, setCardsLoading] = React.useState(true);
  const [totalSales, setTotalSales] = React.useState(0);
  const [totalOrders, setTotalOrders] = React.useState(0);
  const [avgOrderValue, setAvgOrderValue] = React.useState(0);
  const [avgSalesPerSession, setAvgSalesPerSession] = React.useState(0);

  const fetchCardsData = () => {
    setCardsLoading(true);
    posService
      .getDashboardCardsData({
        start_date: (dateRange[0]?.unix() ?? 0) * 1000,
        end_date: (dateRange[1]?.unix() ?? 0) * 1000,
      })
      .then(
        (res: {
          totalSales: number;
          totalOrders: number;
          avgSalesPerSession: number;
          avgOrderValue: number;
        }) => {
          setTotalSales(res.totalSales);
          setTotalOrders(res.totalOrders);
          setAvgOrderValue(res.avgOrderValue);
          setAvgSalesPerSession(res.avgSalesPerSession);
        }
      )
      .catch(() => {
        toast.error("Error happened while fetching dashboard stats");
      })
      .finally(() => {
        setCardsLoading(false);
      });
  };

  useEffect(fetchCardsData, [dateRange]);
  return (
    <Fragment>
      {cardsLoading ? (
        <Grid
          container
          spacing={3}
          sx={{
            m: 0,
            width: "100%",
          }}
        >
          {[1, 2, 3, 4].map((index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={`key - ${index}`}
              sx={{
                p: "12px !important",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(8px)",
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: alpha("#1E8078", 0.1),
                    }}
                  >
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                  </Box>
                </Box>
                <Skeleton
                  variant="text"
                  width="80%"
                  height={36}
                  sx={{ mb: 1 }}
                />
                <Skeleton variant="text" width="40%" height={20} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid
          container
          spacing={3}
          sx={{
            m: 0,
            width: "100%",
          }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={`Total Sales`}
            sx={{
              p: "12px !important",
            }}
          >
            <POSDashboardCard
              color={"#1E8078"}
              value={`BHD ${formatNumber(totalSales)}`}
              icon={"trending_up"}
              description={"Total sales for today"}
              title={"Total Sales"}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={"Total Orders"}
            sx={{
              p: "12px !important",
            }}
          >
            <POSDashboardCard
              color={"#1E8078"}
              value={formatNumber(totalOrders)}
              icon={"shopping_cart"}
              description={"Number of orders processed"}
              title={"Total Orders"}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={"Average Order Value"}
            sx={{
              p: "12px !important",
            }}
          >
            <POSDashboardCard
              color={"#1E8078"}
              value={`BHD ${formatNumber(avgOrderValue)}`}
              icon={"payments"}
              description={"Average value per order"}
              title={"Average Order Value"}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={"Customers Today"}
            sx={{
              p: "12px !important",
            }}
          >
            <POSDashboardCard
              color={"#1E8078"}
              value={formatNumber(avgSalesPerSession)}
              icon={"people"}
              description={"Total customers served"}
              title={"Customers Today"}
            />
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
};
